import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAxios } from '../../utils/hook/useAxios';

import './MobileBottomNav.css';

/* Routes où la barre ne doit pas s'afficher */
const HIDDEN_PATHS = [
  '/signin',
  '/signup',
  '/admin',
  '/forgot-password',
  '/reset-password',
  '/onboarding',
  '/stripe',
  '/inbox/messages/thread', // page chat : layout full-height, input masqué sinon
];

/* Événement envoyé par le Header quand on bascule invité <-> propriétaire */
export const MODE_EVENT = 'mc-mode-change';

/* ── onglets selon le mode ── */
const GUEST_TABS = [
  { to: '/', icon: 'bi-search', label: 'Explorer' },
  {
    to: '/guest/listing/reservations',
    icon: 'bi-calendar-check',
    label: 'Réservations',
    badge: true,
  },
  { to: '/guest/favorites', icon: 'bi-heart', label: 'Favoris' },
  { to: '/guest/settings', icon: 'bi-person-circle', label: 'Profil' },
];

const HOST_TABS = [
  { to: '/account/demandes', icon: 'bi-bell-fill', label: 'Demandes', badge: true },
  { to: '/account/locations', icon: 'bi-key-fill', label: 'Locations' },
  { to: '/account/voitures', icon: 'bi-car-front-fill', label: 'Voitures' },
  { to: '/inbox/messages', icon: 'bi-chat-dots-fill', label: 'Messages' },
  { to: '/business/earnings', icon: 'bi-cash-stack', label: 'Revenus' },
  { to: '/account/settings', icon: 'bi-person-circle', label: 'Profil' },
];

const readMode = () => (window.localStorage.getItem('mode') === 'host' ? 'host' : 'guest');

const readUser = () => {
  try {
    return JSON.parse(window.localStorage.getItem('me') || 'null');
  } catch {
    return null;
  }
};

/**
 * Barre de navigation flottante (mobile uniquement).
 * `mode` est optionnel : sans prop, elle lit localStorage et se met à jour toute seule.
 */
function MobileBottomNav({ mode: modeProp, userId }) {
  const { pathname } = useLocation();
  const axios = useAxios();

  const [storedMode, setStoredMode] = useState(readMode);
  const [badge, setBadge] = useState(0);

  const mode = modeProp ?? storedMode;
  const visible = Boolean(userId) && !HIDDEN_PATHS.some((p) => pathname.startsWith(p));

  /* ── suit le changement de mode (événement Header, autre onglet, changement de page) ── */
  useEffect(() => {
    const sync = () => setStoredMode(readMode());
    window.addEventListener(MODE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(MODE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    setStoredMode(readMode());
  }, [pathname]);

  /* ── place réservée en bas de page (remplace l'ancien <div> spacer) ── */
  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add('has-mobile-bottom-nav');
    return () => document.body.classList.remove('has-mobile-bottom-nav');
  }, [visible]);

  /* ── badge : réservations en attente d'action ── */
  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await axios.get(`/reservations/getbydriverid/${userId}`);
        if (cancelled || !Array.isArray(data)) return;

        // invité : action requise = payer ("accepted") · hôte : accepter ("pending")
        const wanted = mode === 'guest' ? 'accepted' : 'pending';
        setBadge(data.filter((r) => r.status === wanted).length);
      } catch {
        /* silencieux : le badge est un confort */
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, mode, visible, userId]);

  if (!visible) return null;

  const tabs = mode === 'host' ? HOST_TABS : GUEST_TABS;

  const isActive = (to) =>
    to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);

  return (
    <nav
      className={`mc-bottom-nav ${mode === 'host' ? 'is-host' : 'is-guest'}`}
      aria-label="Navigation mobile"
    >
      {/* key={mode} rejoue l'animation à chaque changement de mode */}
      <ul key={mode}>
        {tabs.map(({ to, icon, label, badge: hasBadge }) => {
          const active = isActive(to);

          return (
            <li key={to}>
              <Link
                to={to}
                className={`mc-bottom-nav-item${active ? ' is-active' : ''}`}
                aria-current={active ? 'page' : undefined}
              >
                <span className="mc-bottom-nav-icon">
                  <i className={`bi ${icon}`} aria-hidden="true" />
                  {hasBadge && badge > 0 && (
                    <span className="mc-bottom-nav-badge" aria-label={`${badge} en attente`}>
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </span>
                <span>{label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default MobileBottomNav;
